import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DollarSign, FileText, Search } from 'lucide-react';
import {
  calculatePurityGoldRate,
  calculateNetWeight,
  calculateSaleNetWeight,
  isKundanItemCode,
  calculateFineWeight,
  calculateGoldValue,
  calculateWastageAmount,
  calculateMakingAmount,
  calculateTotalGoldAmount,
  calculateCertificationCharges,
  calculateJewelValue,
  calculateUSDGrandTotal,
  stoneWeightToGrams,
  buildStoneCategoryTotals,
  calculateAcquisitionCostWithTax,
  calculateKundanFineWeight,
  calculateCostPanelResult,
  isSmallItemCode,
  calculateSmallItemPrice,
  applyKundanGoldCostDeduction,
} from '../../shared/utils/jewelryCalculations';
import PageIdentifier from '../shared/PageIdentifier';
import CostPanel, { RatePill } from '../shared/CostPanel';
import SCREEN_IDS from '../../utils/screenIds';
import CustomerLookup from '../shared/CustomerLookup';
import LoadEstimateModal from '../shared/LoadEstimateModal';
import PdfViewerModal from '../shared/PdfViewerModal';
// eslint-disable-next-line no-unused-vars
import PrintService from '../../services/PrintService';
import TwilioWhatsAppService from '../../services/TwilioWhatsAppService';
import PdfService from '../../services/PdfService';
import EmailSendService from '../../services/EmailSendService';
import HistoricalRateService from '../../services/HistoricalRateService';
import EstimateDetailModal from './EstimateDetailModal';
import { convertEstimateToBilling } from '../../utils/estimatePreFill';
import usePermissions from '../../hooks/usePermissions';

// Blur (remove focus) from any input when Enter is pressed
// eslint-disable-next-line no-unused-vars
const blurOnEnter = (e) => { if (e.key === 'Enter') e.target.blur(); };

const USDEstimate = () => {
  // Ref for Item Code input
  const itemCodeRef = React.useRef(null);

  // Auto-focus item code field on page load
  useEffect(() => { itemCodeRef.current?.focus(); }, []);

  // Session-based authentication
  const { hasPermission } = usePermissions();
  const canEditRate = hasPermission('usd-estimate', 'edit');
  const canCreate = hasPermission('usd-estimate', 'create');

  // Categories for wastage/making lookup
  const [categories, setCategories] = useState([]);

  // Section 1: Header & Customer Info
  const [date, setDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  // eslint-disable-next-line no-unused-vars
  const [validUntil, setValidUntil] = useState(() => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30); // 30 days validity
    const yyyy = futureDate.getFullYear();
    const mm = String(futureDate.getMonth() + 1).padStart(2, '0');
    const dd = String(futureDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [pdfViewUrl, setPdfViewUrl] = useState(null);
  const [goldRate, setGoldRate] = useState('');
  const [goldRateEditable, setGoldRateEditable] = useState(false);
  const [dollarRate, setDollarRate] = useState('');
  // Today's live rates, captured once at mount — used to detect when goldRate/dollarRate
  // above have been overridden by a backdated Estimate Date (historical rate lookup).
  const [todaysGoldRate, setTodaysGoldRate] = useState(null);
  const [todaysDollarRate, setTodaysDollarRate] = useState(null);

  // Section 2: Jewelry & Gold Calculation
  const [itemCode, setItemCode] = useState('');
  const [jewelryName, setJewelryName] = useState('');
  const [purity, setPurity] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [fineWeight, setFineWeight] = useState('');
  const [finePercent, setFinePercent] = useState(null); // Fine % from jewelry_pieces (Kundan only)
  const [goldPrice, setGoldPrice] = useState('');
  const [goldValue, setGoldValue] = useState('');
  const [wastagePercent, setWastagePercent] = useState('');
  const [wastageAmount, setWastageAmount] = useState('');
  const [makingCharge, setMakingCharge] = useState('');
  const [makingAmount, setMakingAmount] = useState('');
  const [totalGoldAmount, setTotalGoldAmount] = useState('');

  // Stone calculations
  const [stoneTotal, setStoneTotal] = useState(0);
  const [stoneWeightTotal, setStoneWeightTotal] = useState(0);
  const [stoneCost, setStoneCost] = useState(0);
  const [stonePurchaseCost, setStonePurchaseCost] = useState(0);
  const [fetchedStones, setFetchedStones] = useState([]);
  const currentStonesRef = React.useRef([]); // tracks user-edited stone rates from StoneSection
  const [stoneCategoryTotals, setStoneCategoryTotals] = useState({});
  const [kundanWeight, setKundanWeight] = useState(null);
  const [kundanPercent, setKundanPercent] = useState(null);
  const [origWastagePercent, setOrigWastagePercent] = useState('');
  const [origMakingCharge, setOrigMakingCharge]     = useState('');
  const [origStoneCost, setOrigStoneCost]           = useState(null);
  const [diamondCarats, setDiamondCarats] = useState(0);
  const [certificationRequired, setCertificationRequired] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [jewelryImage, setJewelryImage] = useState(null);

  // Locked entry rates captured when item is scanned (from jewelry_pieces DB columns)
  const [entryRates, setEntryRates] = useState({ goldRate: null, dollarRate: null, shipmentCostUSD: 0, laborCharges: 0, otherCosts: 0, inventoryNetWeight: '' });

  // Grand Total (lifted for WhatsApp sharing)
  const [grandTotalUSD, setGrandTotalUSD] = useState('0.00');
  // Extra totals lifted for PDF generation
  const [pdfTotals, setPdfTotals] = useState({ totalTaxPercent: 13.75, taxTotalINR: '0.00', grandTotalINR: '0.00', certCharges: 0 });
  const [isSending, setIsSending] = useState(false); // eslint-disable-line no-unused-vars

  // Additional fields
  const [notes, setNotes] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [emailStatus, setEmailStatus] = useState('');

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  // Bumped on Clear to force USDEstimateTotalSection to remount, resetting
  // its local state (discount, Customs %) that has no other reset path.
  const [formResetKey, setFormResetKey] = useState(0);
  const [loadedDiscount, setLoadedDiscount] = useState(0);
  const [loadedCustomsPct, setLoadedCustomsPct] = useState(null);
  // Once a matched/loaded estimate is applied, don't keep re-offering the
  // same one back — the customer/item change from loading it would otherwise
  // immediately re-trigger the auto-detect match.
  const [loadedEstimateId, setLoadedEstimateId] = useState(null);
  // Auto-detected existing active estimate for the same customer + item code —
  // offered to staff as "load this instead of starting a new one?"
  const [matchedEstimate, setMatchedEstimate] = useState(null);
  const [matchedEstimateDismissed, setMatchedEstimateDismissed] = useState(false);

  // Debug: Log when showDetailModal changes
  useEffect(() => {
    console.log('🔄 USDEstimate showDetailModal state changed to:', showDetailModal);
  }, [showDetailModal]);

  // Item code lookup status: null | 'found' | 'sold' | 'not_found' | 'out_of_range'
  const [itemCodeStatus, setItemCodeStatus] = useState(null);
  const [itemCodeMsg, setItemCodeMsg] = useState('');
  // Bumped only when starting a new customer (Clear) so USDEstimateTotalSection
  // resets Customs % back to the fetched default rate. Adding another item for
  // the SAME customer must keep any manually-edited Customs % as-is.
  const [customerResetSeq, setCustomerResetSeq] = useState(0);

  // Fetch rates and categories on mount
  const [goldRateError, setGoldRateError] = useState('');
  const [goldRateWarn, setGoldRateWarn] = useState('');

  const catalogPriceINR = useMemo(() => {
    // Small "V" items are priced at a flat cost+30%, not from the breakdown. Their
    // only customer benefit is the Round Off, so the catalog ("Standard Price") is
    // the pre-round-off fixed price; "You Save" then equals just the round-off.
    if (isSmallItemCode(itemCode)) {
      const preRound = parseFloat(pdfTotals?.grandTotalINR) || 0;
      return preRound > 0 ? preRound : null;
    }
    if (origStoneCost === null) return null;
    const origWastageAmt = Math.ceil(calculateWastageAmount(netWeight, origWastagePercent, goldPrice));
    const origMakingAmt  = Math.ceil(calculateMakingAmount(netWeight, origMakingCharge));
    const origTotalGold  = calculateTotalGoldAmount(goldValue, origWastageAmt, origMakingAmt);
    const certAmt        = parseFloat(pdfTotals?.certCharges) || 0;
    const origJewelValue = calculateJewelValue(origTotalGold, origStoneCost, certAmt);
    const taxPct         = parseFloat(pdfTotals?.origTaxPercent) || 13.75;
    const { grandTotalINR } = calculateUSDGrandTotal(origJewelValue, taxPct, parseFloat(dollarRate) || 1);
    return Math.ceil(grandTotalINR);
  }, [origWastagePercent, origMakingCharge, origStoneCost, netWeight, goldPrice, goldValue, pdfTotals, dollarRate, itemCode]);

  useEffect(() => {
    const STALE_DAYS = 2;
    const daysSince = (dateStr) => dateStr
      ? Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
      : null;

    // Fetch gold rate
    fetch('/api/rates/gold/today', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate && data.rate.gold_24k != null && data.rate.gold_24k !== '') {
          setGoldRate(parseFloat(data.rate.gold_24k));
          setTodaysGoldRate(parseFloat(data.rate.gold_24k));
          setGoldRateEditable(false);
          const age = daysSince(data.rate.rate_date);
          if (!data.is_today && age !== null && age > STALE_DAYS) {
            setGoldRateError('');
            setGoldRateWarn('');
            setGoldRateEditable(true);
          } else if (!data.is_today) {
            setGoldRateWarn(`ℹ️ ${data.message}. Rate is auto-filled — you can edit if needed.`);
            setGoldRateError('');
            setGoldRateEditable(true);
          } else {
            setGoldRateError('');
            setGoldRateWarn('');
          }
        } else {
          setGoldRate('');
          setGoldRateError('Gold rate not found. Please enter rate manually.');
          setGoldRateWarn('');
          setGoldRateEditable(true);
        }
      })
      .catch(() => {
        // Network/server error reaching our own API — keep showing whatever rate was
        // already loaded (last known good rate) instead of blanking the field.
        setGoldRateError('Failed to fetch latest gold rate. Showing last known rate — please verify or enter manually.');
        setGoldRateWarn('');
        setGoldRateEditable(true);
      });

    // Fetch dollar rate
    fetch('/api/rates/dollar/today', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate) {
          const rawRate = parseFloat(data.rate.usd_to_inr);
          const adjusted = rawRate > 0 ? Math.floor(rawRate - 3) : rawRate;
          setDollarRate(adjusted);
          setTodaysDollarRate(adjusted);
        }
      })
      .catch(() => {
        // Keep default value on error
      });

    // Fetch categories
    fetch('/api/categories', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCategories(data);
        }
      })
      .catch(() => {
        setCategories([]);
      });
  }, []);

  const costPanelNetWeight = useMemo(() => {
    const result = calculateNetWeight(grossWeight, stoneWeightTotal);
    return result !== null ? result.toFixed(3) : '';
  }, [grossWeight, stoneWeightTotal]);

  // Sale net weight includes purity-converted Kundan weight.
  useEffect(() => {
    const result = calculateSaleNetWeight({ grossWeight, stoneWeightTotal, itemCode, kundanWeight, purity });
    setNetWeight(parseFloat(grossWeight) > 0 ? result.toFixed(3) : '');
  }, [grossWeight, stoneWeightTotal, itemCode, kundanWeight, purity]);

  // Calculate fine weight
  useEffect(() => {
    if (netWeight && purity) {
      setFineWeight(calculateFineWeight(netWeight, purity).toFixed(3));
    } else {
      setFineWeight('');
    }
  }, [netWeight, purity]);

  // Calculate purity-adjusted gold price
  useEffect(() => {
    if (goldRate && purity) {
      setGoldPrice(calculatePurityGoldRate(goldRate, purity).toFixed(2));
    } else {
      setGoldPrice('');
    }
  }, [goldRate, purity]);

  // Calculate gold value using Net Weight
  useEffect(() => {
    if (goldPrice && netWeight) {
      setGoldValue(calculateGoldValue(goldPrice, netWeight).toFixed(2));
    } else {
      setGoldValue('');
    }
  }, [goldPrice, netWeight]);

  // Calculate wastage amount using Net Weight (always rounds up — consistent with all other screens)
  useEffect(() => {
    if (netWeight && wastagePercent && goldPrice) {
      setWastageAmount(calculateWastageAmount(netWeight, wastagePercent, goldPrice).toFixed(3));
    } else {
      setWastageAmount('');
    }
  }, [netWeight, wastagePercent, goldPrice]);

  // Calculate making amount
  useEffect(() => {
    if (netWeight && makingCharge) {
      setMakingAmount(String(Math.ceil(calculateMakingAmount(netWeight, makingCharge))));
    } else {
      setMakingAmount('');
    }
  }, [netWeight, makingCharge]);

  // Calculate total gold amount
  useEffect(() => {
    setTotalGoldAmount(calculateTotalGoldAmount(goldValue, wastageAmount, makingAmount).toFixed(2));
  }, [goldValue, wastageAmount, makingAmount]);

  const handleStoneTotalChange = useCallback((stoneData) => {
    setStoneWeightTotal(stoneData.weight || 0);
    setStoneCost(stoneData.cost || 0);
    setStonePurchaseCost(stoneData.purchaseTotal || 0);
    if (stoneData.categoryTotals) setStoneCategoryTotals(stoneData.categoryTotals);
    if (stoneData.stones) currentStonesRef.current = stoneData.stones;
  }, []);

  // Fetch jewelry details by Item Code
  const clearJewelryFields = () => {
    setJewelryName('');
    setPurity('');
    setGrossWeight('');
    setNetWeight('');
    setFineWeight('');
    setFinePercent(null);
    setJewelryImage(null);
    setFetchedStones([]);
    setDiamondCarats(0);
    setStoneTotal(0);
    setStoneWeightTotal(0);
    setStoneCost(0);
    setStonePurchaseCost(0);
    setStoneCategoryTotals({});
    setCertificationRequired(false);
    setCategoryInfo(null);
    setWastagePercent('');
    setMakingCharge('');
    setEntryRates({ goldRate: null, dollarRate: null, shipmentCostUSD: 0 });
    setKundanWeight(null);
    setKundanPercent(null);
    setOrigWastagePercent('');
    setOrigMakingCharge('');
    setOrigStoneCost(null);
  };

  const closePdfView = () => {
    if (pdfViewUrl) URL.revokeObjectURL(pdfViewUrl);
    setPdfViewUrl(null);
  };

  const handleDateChange = async (event) => {
    const selectedDate = event.target.value;
    setDate(selectedDate);
    if (!selectedDate) return;
    try {
      const { gold, dollar } = await HistoricalRateService.getRates(selectedDate);
      const missing = [];
      if (gold?.gold_24k != null && gold.gold_24k !== '') {
        setGoldRate(parseFloat(gold.gold_24k));
        setGoldRateError('');
      } else {
        missing.push('gold');
      }
      if (dollar?.usd_to_inr != null && dollar.usd_to_inr !== '') {
        const rawRate = parseFloat(dollar.usd_to_inr);
        setDollarRate(rawRate > 0 ? Math.floor(rawRate - 3) : rawRate);
      } else {
        missing.push('dollar');
      }
      setGoldRateWarn(missing.length
        ? `No ${missing.join(' or ')} rate saved for ${selectedDate}. Existing values were kept.`
        : `Gold and dollar rates updated for ${selectedDate}.`);
      setGoldRateEditable(true);
    } catch (error) {
      setGoldRateError(error.message || 'Failed to fetch rates for selected date.');
      setGoldRateWarn('');
      setGoldRateEditable(true);
    }
  };

  const fetchJewelryDetails = async (code) => {
    if (!code) return;
    setItemCodeStatus(null);
    setItemCodeMsg('');
    try {
      const normalizedCode = code.trim().toUpperCase();
      const categoryCode = normalizedCode.split('-')[0];

      const res = await fetch(`/api/jewelry/details/${normalizedCode}`, { credentials: 'include' });
      const data = await res.json();

      // ── Handle error responses ──────────────────────────────────────────────
      if (!res.ok) {
        clearJewelryFields();
        if (data.error === 'item_status') {
          setItemCodeStatus('sold');
          setItemCodeMsg(`"${normalizedCode}" has already been ${String(data.status).toLowerCase()} and cannot be estimated. If the customer is interested in a similar item, enter that item's code instead.`);
        } else if (data.error === 'out_of_range') {
          setItemCodeStatus('out_of_range');
          setItemCodeMsg(data.message || `Item "${normalizedCode}" does not exist.`);
        } else {
          setItemCodeStatus('not_found');
          setItemCodeMsg(`Item "${normalizedCode}" was not found in the inventory.`);
        }
        itemCodeRef.current?.focus();
        return;
      }

      // ── Item found and available ────────────────────────────────────────────
      if (data && data.code === normalizedCode) {
        setItemCodeStatus('found');
        setItemCodeMsg('');
        setJewelryName(data.description?.trim() || data.code || ''); // description, else item code
        setPurity(data.gold_purity || '');
        setGrossWeight(data.gross_weight || '');
        setNetWeight(data.net_weight || '');
        setFinePercent(data.fine_percentage ? parseFloat(data.fine_percentage) : null);
        setKundanWeight(data.kundan_weight  ? parseFloat(data.kundan_weight)  : null);
        setKundanPercent(data.kundan_percentage ? parseFloat(data.kundan_percentage) : null);
        setJewelryImage(data.image_url || null);
        setEntryRates({
          goldRate: data.gold_rate_at_entry ? parseFloat(data.gold_rate_at_entry) : null,
          dollarRate: data.dollar_rate_at_entry ? parseFloat(data.dollar_rate_at_entry) : null,
          shipmentCostUSD: data.shipment_cost_usd ? parseFloat(data.shipment_cost_usd) : 0,
          laborCharges: data.labor_charges ? parseFloat(data.labor_charges) : 0,
          otherCosts: data.other_costs ? parseFloat(data.other_costs) : 0,
          inventoryNetWeight: data.net_weight || ''
        });

        setCertificationRequired(data.certificate === 'Yes');

        const stones = data.stones || [];
        setFetchedStones(stones);

        // Use category totals from API response
        if (data.stoneCategoryTotals) {
          setStoneCategoryTotals(data.stoneCategoryTotals);
          // Diamond weight — sum ALL entries in the Diamond or Flat Diamond categories
          // (matches USBilling's calculateDiamondCarats, which also covers both categories).
          // Use rawWeight (carats) NOT weight (gram-converted) — cert charges are per carat.
          const diamondWeight = Object.values(data.stoneCategoryTotals)
            .filter(cat => cat.category === 'Diamond' || cat.category === 'Flat Diamond')
            .reduce((sum, cat) => sum + (parseFloat(cat.rawWeight) || 0), 0);
          setDiamondCarats(diamondWeight);

          // Total stone weight across ALL categories (already gram-converted by server)
          const totalStoneWeightGrams = Object.values(data.stoneCategoryTotals)
            .reduce((sum, cat) => sum + (parseFloat(cat.weight) || 0), 0);
          setStoneTotal(totalStoneWeightGrams);

          const catalogStoneCost = Object.values(data.stoneCategoryTotals)
            .reduce((sum, cat) => sum + (parseFloat(cat.cost) || 0), 0);
          setOrigStoneCost(catalogStoneCost);

          console.log('📊 Category Totals from API:', {
            diamondWeight,
            totalStoneWeightGrams,
            allCategories: Object.keys(data.stoneCategoryTotals)
          });
        } else if (data.certificate === 'Yes') {
          // Fallback to old calculation if category totals not available
          await calculateDiamondCarats(data.id, stones);
          setStoneTotal(0);
          setOrigStoneCost(0);
        } else {
          setDiamondCarats(0);
          setStoneTotal(0);
          setOrigStoneCost(0);
        }
      } else {
        clearJewelryFields();
        setItemCodeStatus('not_found');
        setItemCodeMsg(`Item "${normalizedCode}" was not found in the inventory.`);
        itemCodeRef.current?.focus();
      }

      // Find category by code
      if (data && data.code === normalizedCode && categoryCode && categories.length > 0) {
        const cat = categories.find(c => c.code === categoryCode);
        if (cat) {
          // Inherit from parent if child wastage or making is 0 / not set
          const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
          const effectiveWastage = parseFloat(cat.sale_wastage_pct) > 0
            ? cat.sale_wastage_pct
            : (parent ? parent.sale_wastage_pct : '');
          const effectiveMaking = parseFloat(cat.sale_making_per_g) > 0
            ? cat.sale_making_per_g
            : (parent ? parent.sale_making_per_g : '');
          setCategoryInfo(cat);
          setWastagePercent(effectiveWastage || '');
          setMakingCharge(effectiveMaking || '');
          setOrigWastagePercent(effectiveWastage || '');
          setOrigMakingCharge(effectiveMaking || '');
        } else {
          setCategoryInfo(null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch jewelry details', err);
      clearJewelryFields();
      setItemCodeStatus('not_found');
      setItemCodeMsg('Network error. Please try again.');
      itemCodeRef.current?.focus();
    }
  };

  // Calculate diamond carats
  const calculateDiamondCarats = async (jewelryId, stones) => {
    try {
      const diamondRes = await fetch('/api/materials/category/Diamond', { credentials: 'include' });
      const diamondData = await diamondRes.json();

      if (diamondData && Array.isArray(diamondData)) {
        const diamondCodes = diamondData.map(material => material.code);
        const diamondStones = stones.filter(stone =>
          diamondCodes.includes(stone.stone_code)
        );
        const totalDiamondCarats = diamondStones.reduce((total, stone) =>
          total + (parseFloat(stone.weight) || 0), 0
        );
        setDiamondCarats(totalDiamondCarats);
      } else {
        setDiamondCarats(0);
      }
    } catch (err) {
      console.error('Failed to calculate diamond carats:', err);
      setDiamondCarats(0);
    }
  };

  // Save estimate
  const saveEstimate = async () => {
    try {
      setSaveStatus('saving');

      const taxPercent        = parseFloat(pdfTotals?.totalTaxPercent) || 13.75;
      const certAmt           = parseFloat(pdfTotals?.certCharges) || 0;
      const goldAmt           = Math.ceil(parseFloat(totalGoldAmount || 0));
      const stoneAmt          = Math.ceil(parseFloat(stoneTotal || 0));
      const jewelValue        = goldAmt + stoneAmt + certAmt;
      const taxAmtINR         = parseFloat(pdfTotals?.taxTotalINR) || Math.ceil((jewelValue * taxPercent) / 100);
      const preDiscountINR    = parseFloat(pdfTotals?.grandTotalINR) || (jewelValue + taxAmtINR);
      const discountUSD       = parseFloat(pdfTotals?.discountAmount) || 0;
      const discountINR       = Math.round(discountUSD * (parseFloat(dollarRate) || 1));
      const grandTotalINRFinal = Math.max(0, Math.ceil(preDiscountINR - discountINR));
      const grandTotalUSDFinal = parseFloat(grandTotalUSD) || 0;

      const currentStones = currentStonesRef.current;
      const stoneDetails = (currentStones.length > 0 ? currentStones : fetchedStones).map(stone => {
        const code   = stone.stone_code || stone.code || '';
        const name   = stone.stone_name || stone.name || '';
        const weight = parseFloat(stone.weight) || 0;
        const rate   = parseFloat(stone.rate ?? stone.sale_price) || 0;
        return { id: stone.id, code, name, weight, unit: stone.unit || 'gram', rate, cost: weight * rate };
      });

      const estimateData = {
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        customer_city: customerCity,
        customer_state: customerState,
        item_code: itemCode,
        jewelry_name: jewelryName,
        category_id: categoryInfo?.id || null,
        gold_purity: parseFloat(purity) || null,
        gross_weight: parseFloat(grossWeight) || null,
        net_weight: parseFloat(netWeight) || null,
        fine_weight: parseFloat(fineWeight) || null,
        gold_rate: parseFloat(goldRate) || null,
        gold_price: parseFloat(goldPrice) || null,
        gold_value: parseFloat(goldValue) || null,
        wastage_percent: parseFloat(wastagePercent) || null,
        wastage_amount: parseFloat(wastageAmount) || null,
        making_charge_per_gram: parseFloat(makingCharge) || null,
        making_amount: parseFloat(makingAmount) || null,
        total_gold_amount: parseFloat(totalGoldAmount) || null,
        stone_total: parseFloat(stoneTotal) || null,
        stone_details: stoneDetails,
        diamond_carats: parseFloat(diamondCarats) || null,
        certification_required: certificationRequired,
        certification_charges: certAmt,
        currency: 'USD',
        usd_to_inr_rate: parseFloat(dollarRate) || null,
        tax_rate: taxPercent,
        tax_amount: taxAmtINR,
        subtotal: jewelValue,
        discount_amount: discountUSD,
        grand_total: grandTotalINRFinal,
        grand_total_inr: grandTotalINRFinal,
        grand_total_usd: grandTotalUSDFinal,
        valid_until: validUntil,
        notes: notes,
        catalog_price_inr: catalogPriceINR
      };

      const response = await fetch('/api/estimates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(estimateData)
      });

      const result = await response.json();

      if (result.success) {
        setSaveStatus(result.updated ? 'updated' : 'saved');
        setTimeout(() => setSaveStatus(''), 3000);
        return {
          estimate: result.estimate || result,
          discountINR,
          discountUSD,
          grandTotalINRFinal,
          grandTotalUSDFinal,
          taxPercent,
          taxAmtINR,
          certAmt,
        };
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
        throw new Error(result.error || result.message || 'Failed to save estimate');
      }
    } catch (error) {
      console.error('Error saving estimate:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
      throw error;
    }
  };

  // Handle view — generates PDF and opens in viewer modal
  const handleView = async () => {
    if (!customerName) {
      alert('Please enter customer name before viewing');
      return;
    }

    try {
      const { estimate, discountINR, grandTotalINRFinal, taxPercent, taxAmtINR, certAmt } = await saveEstimate();
      if (!estimate) return;

      const pdfUrl = await PdfService.getEstimatePdfUrl({
        type: 'Estimate',
        currency: 'USD',
        companyName: 'GDS Jewellers',
        companyTagline: 'Hallmarked Certified Jewellery',
        date,
        validUntil,
        estimateNumber: estimate?.estimate_number || '',
        customerName,
        customerPhone,
        customerEmail,
        customerCity,
        customerState,
        itemCode,
        jewelryName,
        purity,
        grossWeight,
        netWeight,
        fineWeight,
        goldRate,
        goldPrice,
        goldValue,
        wastagePercent,
        wastageAmount,
        makingCharge,
        makingAmount,
        totalGoldAmount,
        stones: fetchedStones,
        stoneCategoryTotals,
        stoneTotal,
        certificationCharges: certAmt,
        customsDutyPercent: taxPercent,
        taxLabel: `Customs (${taxPercent || 0}%)`,
        taxAmount: taxAmtINR,
        dollarRate,
        grandTotal: grandTotalINRFinal,
        grandTotalINR: grandTotalINRFinal,
        grandTotalUSD,
        notes,
        image_url: jewelryImage,
        discount: discountINR,
        catalogPriceINR,
        modifiedPriceINR: grandTotalINRFinal,
      }, 'USD');
      setPdfViewUrl(pdfUrl);
    } catch (err) {
      console.error('handleView error:', err);
      alert('Failed to generate PDF: ' + err.message);
    }
  };

  // Handle WhatsApp with save
  const handleWhatsApp = async () => {
    if (!customerName) {
      alert('Please enter customer name before sharing');
      return;
    }

    try {
      const { estimate, discountINR, grandTotalINRFinal } = await saveEstimate();
      await TwilioWhatsAppService.sendEstimate(estimate.id, {
        catalogPriceINR,
        modifiedPriceINR: grandTotalINRFinal,
        discountINR,
      });
    } catch (error) {
      alert('Failed to share estimate via WhatsApp: ' + error.message);
    }
  };

  // Handle email estimate
  const handleEmail = async () => {
    if (!customerName) {
      alert('Please enter customer name');
      return;
    }
    if (!customerEmail) {
      alert('Please enter customer email address');
      return;
    }
    try {
      setIsSending(true);
      const { estimate, discountINR, grandTotalINRFinal, grandTotalUSDFinal, taxPercent, taxAmtINR, certAmt } = await saveEstimate();

      await EmailSendService.sendEmail({
        currency: 'USD',
        type: 'Estimate',
        companyName: 'GDS Jewellers',
        companyTagline: 'Hallmarked Certified Jewellery',
        estimateNumber: estimate?.estimate_number || '',
        date,
        validUntil,
        customerName,
        customerPhone,
        customerEmail,
        customerCity,
        customerState,
        itemCode,
        jewelryName,
        purity,
        grossWeight,
        netWeight,
        fineWeight,
        goldRate,
        goldPrice,
        goldValue,
        wastagePercent,
        wastageAmount,
        makingCharge,
        makingAmount,
        totalGoldAmount,
        stones: fetchedStones,
        stoneCategoryTotals,
        stoneTotal,
        certificationCharges: certAmt,
        customsDutyPercent: taxPercent,
        taxLabel: `Customs (${taxPercent}%)`,
        taxAmount: taxAmtINR,
        dollarRate,
        grandTotal: grandTotalINRFinal,
        grandTotalINR: grandTotalINRFinal,
        grandTotalUSD: grandTotalUSDFinal,
        notes,
        image_url: jewelryImage,
        discount: discountINR,
        catalogPriceINR,
        modifiedPriceINR: grandTotalINRFinal,
      }, customerEmail, { silent: true });
      setEmailStatus('sent');
    } catch (error) {
      setEmailStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!customerName) {
      alert('Please enter customer name before downloading PDF');
      return;
    }
    try {
      const { estimate, discountINR, grandTotalINRFinal, grandTotalUSDFinal, taxPercent, taxAmtINR, certAmt } = await saveEstimate();
      if (!estimate) return;
      await PdfService.downloadEstimatePDF({
        currency: 'USD',
        companyName: 'GDS Jewellers',
        companyTagline: 'Hallmarked Certified Jewellery',
        estimateNumber: estimate?.estimate_number || '',
        date,
        validUntil,
        customerName,
        customerPhone,
        customerEmail,
        customerCity,
        customerState,
        itemCode,
        jewelryName,
        purity,
        grossWeight,
        netWeight,
        fineWeight,
        goldRate,
        goldPrice,
        goldValue,
        wastagePercent,
        wastageAmount,
        makingCharge,
        makingAmount,
        totalGoldAmount,
        stones: fetchedStones,
        stoneCategoryTotals,
        stoneTotal,
        certificationCharges: certAmt,
        customsDutyPercent: taxPercent,
        taxLabel: `Customs (${taxPercent || 0}%)`,
        taxAmount: taxAmtINR,
        dollarRate,
        grandTotal: grandTotalINRFinal,
        grandTotalINR: grandTotalINRFinal,
        grandTotalUSD: grandTotalUSDFinal,
        notes,
        image_url: jewelryImage,
        discount: discountINR,
        catalogPriceINR,
        modifiedPriceINR: grandTotalINRFinal,
      }, 'USD');
    } catch (err) {
      console.error('handleDownloadPDF error:', err);
      alert('Failed to generate PDF: ' + err.message);
    }
  };

  // Save the estimate, then jump straight to Billing pre-filled with it —
  // completes the estimate→sale cycle without a trip through View Estimates.
  const handleConvertToBilling = async () => {
    if (!customerName) {
      alert('Please enter customer name before converting to billing');
      return;
    }
    try {
      const { estimate, taxPercent, discountUSD } = await saveEstimate();
      if (!estimate) return;
      // Pass the exact on-screen (just-saved) Customs % and discount as
      // explicit overrides rather than relying solely on what round-trips
      // back through the saved estimate row — guarantees Billing loads with
      // precisely what was just finalized with the customer, not a default.
      convertEstimateToBilling(estimate, { customsPct: taxPercent, discount: discountUSD });
    } catch (err) {
      console.error('handleConvertToBilling error:', err);
      alert('Failed to save estimate: ' + err.message);
    }
  };

  // Populate the form from a previously-saved estimate (manual "Load Estimate"
  // search, or the auto-detected match banner below).
  const handleLoadEstimate = async (est) => {
    setCustomerName(est.customer_name || '');
    setCustomerPhone(est.customer_phone || '');
    setCustomerEmail(est.customer_email || '');
    setCustomerCity(est.customer_city || '');
    setCustomerState(est.customer_state || '');

    // Gold rate — use whichever is higher (today's live rate vs the estimate's quoted rate)
    const sysGold = parseFloat(goldRate) || 0;
    const estGold = parseFloat(est.gold_rate) || 0;
    const appliedGold = Math.max(sysGold, estGold);
    if (appliedGold > 0) {
      setGoldRate(appliedGold);
      setGoldRateEditable(true);
    }

    // stone_details is a JSONB column — normally comes back already parsed,
    // but has been observed as a raw JSON string too (see ViewEstimates.js's
    // detail-modal fetch), so parse defensively either way.
    let rawStoneDetails = est.stone_details;
    if (typeof rawStoneDetails === 'string') {
      try { rawStoneDetails = JSON.parse(rawStoneDetails); } catch { rawStoneDetails = []; }
    }
    const estStones = Array.isArray(rawStoneDetails) && rawStoneDetails.length > 0
      ? rawStoneDetails.map(s => ({
          stone_code: s.stone_code || s.code || '',
          stone_name: s.stone_name || s.name || '',
          weight: s.weight,
          unit: s.unit || 'gram',
          sale_price: s.sale_price ?? s.rate ?? 0,
        }))
      : null;
    const estWastage = est.wastage_percent ? String(est.wastage_percent) : null;
    const estMaking = est.making_charge_per_gram ? String(est.making_charge_per_gram) : null;
    setLoadedDiscount(parseFloat(est.discount_amount) || 0);
    setLoadedCustomsPct(est.tax_rate != null ? parseFloat(est.tax_rate) : null);
    setLoadedEstimateId(est.id || null);

    if (est.item_code) {
      setItemCode(est.item_code);
      await fetchJewelryDetails(est.item_code);
    }
    // Apply the estimate's own saved rates/stones on top of the item's defaults
    if (estStones) setFetchedStones(estStones);
    if (estWastage) setWastagePercent(estWastage);
    if (estMaking) setMakingCharge(estMaking);

    setShowLoadModal(false);
    setMatchedEstimate(null);
  };

  // Reset auto-match dismissal whenever staff change who/what they're estimating.
  useEffect(() => {
    setMatchedEstimateDismissed(false);
  }, [itemCode, customerPhone, customerName]);

  // Auto-detect: does this customer + item code already match a saved active
  // estimate? If so, offer to load it instead of starting a duplicate.
  useEffect(() => {
    if (matchedEstimateDismissed || itemCodeStatus !== 'found') {
      setMatchedEstimate(null);
      return;
    }
    const query = (customerPhone || customerName || '').trim();
    if (query.length < 3) {
      setMatchedEstimate(null);
      return;
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/estimates?customer=${encodeURIComponent(query)}&status=active&currency=USD&limit=50`, { credentials: 'include' });
        const data = await res.json();
        if (cancelled || !data.success) return;
        const normalizedCode = itemCode.trim().toUpperCase();
        const match = (data.estimates || []).find(e => (e.item_code || '').trim().toUpperCase() === normalizedCode);
        // Don't re-offer the estimate that was just loaded — loading it
        // changes customer/item to values that would otherwise immediately
        // re-match against itself.
        setMatchedEstimate(match && match.id !== loadedEstimateId ? match : null);
      } catch {
        if (!cancelled) setMatchedEstimate(null);
      }
    }, 500);

    return () => { cancelled = true; clearTimeout(t); };
  }, [itemCode, itemCodeStatus, customerPhone, customerName, matchedEstimateDismissed, loadedEstimateId]);

  // Handle view estimate
  // eslint-disable-next-line no-unused-vars
  const handleViewEstimate = () => {
    if (!itemCode) {
      alert('Please fill in the item code to view estimate');
      return;
    }
    setShowDetailModal(true);
  };

  const clearForm = () => {
    setItemCode('');
    setJewelryName('');
    setPurity('');
    setGrossWeight('');
    setNetWeight('');
    setFineWeight('');
    setFinePercent(null);
    setGoldPrice('');
    setGoldValue('');
    setWastagePercent('');
    setWastageAmount('');
    setMakingCharge('');
    setMakingAmount('');
    setTotalGoldAmount('');
    setFetchedStones([]);
    setStoneTotal(0);
    setDiamondCarats(0);
    setCertificationRequired(false);
    setCategoryInfo(null);
    setJewelryImage(null);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setCustomerCity('');
    setCustomerState('');
    setNotes('');
    setKundanWeight(null);
    setKundanPercent(null);
    setOrigWastagePercent('');
    setOrigMakingCharge('');
    setOrigStoneCost(null);
    setEmailStatus('');
    setPdfViewUrl(null);
    setLoadedDiscount(0);
    setLoadedCustomsPct(null);
    setLoadedEstimateId(null);
    setFormResetKey(k => k + 1);
    currentStonesRef.current = [];
    setCustomerResetSeq(seq => seq + 1);
    if (itemCodeRef.current) {
      itemCodeRef.current.focus();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageIdentifier pageId={SCREEN_IDS?.ESTIMATES?.USD_ESTIMATE || 'EST-002'} pageName="USD Estimate" />

      {/* ── Sticky Action Bar ── */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="px-6 py-2.5 flex items-center justify-between gap-3 max-w-6xl mx-auto">

          {/* Left: Title */}
          <h1 className="text-base font-bold text-slate-800 whitespace-nowrap flex items-center gap-2">
            <DollarSign size={16} className="text-green-500" />
            USD Estimate
          </h1>

          {/* Centre: Actions */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setShowLoadModal(true)} disabled={!canCreate}
              title="Search for an existing customer's saved estimate to load and edit"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 disabled:opacity-40 transition">
              <Search size={13} /> Load Estimate
            </button>
            <button onClick={handleView} disabled={!canCreate || !customerName || saveStatus === 'saving'}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-600 hover:bg-slate-700 text-white disabled:opacity-40 transition">
              <FileText size={13} /> View
            </button>
            <PdfViewerModal url={pdfViewUrl} onClose={closePdfView} />
            <button onClick={handleWhatsApp} disabled={!canCreate || !customerName || saveStatus === 'saving'}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 transition">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
              WhatsApp
            </button>
            <button onClick={handleEmail} disabled={!canCreate || !customerName || !customerEmail || saveStatus === 'saving'}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 transition">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              Email
            </button>
            {emailStatus && (
              <span className={`px-2.5 py-1.5 rounded-md text-xs font-medium ${emailStatus === 'sent' ? 'bg-purple-50 text-purple-700' : 'bg-red-50 text-red-700'}`}>
                {emailStatus === 'sent' ? '✓ Email Sent' : '✗ Email Error'}
              </span>
            )}
            <button onClick={handleDownloadPDF} disabled={!canCreate || !customerName || saveStatus === 'saving'}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 transition">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
              PDF
            </button>
            <button onClick={handleConvertToBilling} disabled={!canCreate || !customerName || !itemCode || saveStatus === 'saving'}
              title="Save this estimate and go straight to Billing to complete the sale"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 transition">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              Convert to Billing
            </button>
            <button onClick={clearForm}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 transition">
              Clear
            </button>
            {saveStatus && (
              <span className={`px-2.5 py-1.5 rounded-md text-xs font-medium ${saveStatus === 'saved' || saveStatus === 'updated' ? 'bg-emerald-50 text-emerald-700' :
                saveStatus === 'error' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                }`}>
                {saveStatus === 'saved' && '✓ Saved'}
                {saveStatus === 'updated' && '✓ Updated existing estimate for this customer'}
                {saveStatus === 'error' && '✗ Error'}
                {saveStatus === 'saving' && '… Saving'}
              </span>
            )}
          </div>

          {/* Right: Rate pills + Date */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-amber-600 whitespace-nowrap">24K</span>
              <div className="w-px h-4 bg-amber-200" />
              {goldRateEditable ? (
                <div className="flex items-center gap-0.5">
                  <span className="text-sm font-bold text-amber-800">₹</span>
                  <input type="number" onFocus={e => e.target.select()} value={goldRate} onChange={(e) => setGoldRate(e.target.value)}
                    className="w-20 text-sm font-bold focus:outline-none bg-transparent text-amber-800 border-b border-amber-400" />
                </div>
              ) : (
                <span className="text-sm font-bold text-amber-800 whitespace-nowrap">
                  ₹{goldRate ? parseFloat(goldRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-green-600 whitespace-nowrap">$1=₹</span>
              <input type="number" onFocus={e => e.target.select()} value={dollarRate} onChange={(e) => setDollarRate(e.target.value)}
                className="w-14 text-sm font-bold focus:outline-none bg-transparent text-green-800 border-b border-green-400" />
            </div>
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input type="date" value={date} onChange={handleDateChange}
                className="text-xs font-medium text-slate-700 focus:outline-none bg-transparent cursor-pointer" />
            </div>
          </div>
        </div>
        {(goldRateError || goldRateWarn) && (
          <div className="px-6 pb-1.5 max-w-6xl mx-auto flex gap-3">
            {goldRateError && <p className="text-xs text-red-500">{goldRateError}</p>}
            {goldRateWarn && <p className="text-xs text-amber-500">{goldRateWarn}</p>}
          </div>
        )}
      </div>

      {/* ── Auto-detected existing estimate for this customer + item ── */}
      {matchedEstimate && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between gap-3 print:hidden">
          <span className="text-xs text-amber-800 font-medium">
            📋 {matchedEstimate.customer_name} already has an active estimate <strong>{matchedEstimate.estimate_number}</strong> for this item (${parseFloat(matchedEstimate.grand_total_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Load it instead of starting a new one?
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => handleLoadEstimate(matchedEstimate)}
              className="text-xs font-semibold px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white transition">
              Load It
            </button>
            <button onClick={() => setMatchedEstimateDismissed(true)}
              className="text-amber-500 hover:text-amber-700 text-xs underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {showLoadModal && (
        <LoadEstimateModal
          currency="USD"
          onClose={() => setShowLoadModal(false)}
          onLoad={handleLoadEstimate}
        />
      )}

      {/* ── Body ── */}
      <div className="px-6 py-3 pb-4 max-w-7xl mx-auto">

        {/* ── ROW 1: Customer — full width ── */}
        <div className="mb-3">
          <div className="relative z-10 bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-600 rounded-t-xl">
              <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-white font-semibold text-xs tracking-widest uppercase">Customer</span>
            </div>
            <div className="px-5 py-3">
              <CustomerLookup
                customerName={customerName} customerPhone={customerPhone}
                customerEmail={customerEmail} customerCity={customerCity} customerState={customerState}
                country="United States"
                onChange={(field, value) => {
                  if (field === 'customerName') setCustomerName(value)
                  else if (field === 'customerPhone') setCustomerPhone(value)
                  else if (field === 'customerEmail') setCustomerEmail(value)
                  else if (field === 'customerCity') setCustomerCity(value)
                  else if (field === 'customerState') setCustomerState(value)
                }}
                onCustomerLoaded={() => setCustomerResetSeq(seq => seq + 1)}
              />
            </div>
          </div>
        </div>

        {/* ── ROW 2: Left (Item + Gold + Stones + Notes) | Right (Price Breakdown + Image) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 items-start">

          {/* ══ LEFT COLUMN ══ */}
          <div className="xl:col-span-2 flex flex-col gap-3">

            {/* Item */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-600 rounded-t-xl">
                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Item</span>
              </div>
              <div className="px-4 py-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Item Code</label>
                    <input type="text" value={itemCode} ref={itemCodeRef}
                      onChange={e => { setItemCode(e.target.value.toUpperCase()); setItemCodeStatus(null); setItemCodeMsg(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchJewelryDetails(itemCode); e.target.blur(); } }}
                      className="w-full border-2 border-amber-300 rounded-lg px-3 py-1.5 text-sm bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono font-semibold"
                      placeholder="e.g. DBRM-40" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Description</label>
                    <input type="text" value={jewelryName} onChange={e => setJewelryName(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                      placeholder="Name" />
                  </div>
                </div>
                {itemCodeStatus && itemCodeStatus !== 'found' && (
                  <div className={`mt-2 flex items-start gap-2 rounded-lg px-3 py-1.5 text-xs font-medium border
                    ${itemCodeStatus === 'sold' ? 'bg-red-50 border-red-200 text-red-700' : ''}
                    ${itemCodeStatus === 'out_of_range' ? 'bg-amber-50 border-amber-200 text-amber-700' : ''}
                    ${itemCodeStatus === 'not_found' ? 'bg-slate-50 border-slate-200 text-slate-500' : ''}
                  `}>
                    <span className="text-base leading-none mt-0.5">
                      {itemCodeStatus === 'sold' ? '🚫' : itemCodeStatus === 'out_of_range' ? '⚠️' : '🔍'}
                    </span>
                    <span>{itemCodeMsg}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Gold */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-600 rounded-t-xl">
                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Gold</span>
              </div>
              <div className="px-4 py-3">
                {/* Weights row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  {[
                    ['Gross Wt (g)', grossWeight],
                    ['Net Wt (g)', netWeight],
                    [purity ? `${purity}K Rate (₹/g)` : 'Gold Rate (₹/g)', goldPrice ? `₹${parseFloat(goldPrice).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''],
                    ['Fine Wt (g)', fineWeight],
                  ].map(([lbl, val]) => (
                    <div key={lbl}>
                      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{lbl}</label>
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {val || <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Purity / Gold Price / Gold Value */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Purity</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {purity ? `${purity}K` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  {[['Gold Price (₹/g)', goldPrice], ['Gold Value (₹)', goldValue]].map(([lbl, val]) => (
                    <div key={lbl}>
                      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{lbl}</label>
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {val ? `₹${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {/* VA % | VA Amt | Making ₹/g | Making Amt */}
                <div className="grid grid-cols-4 gap-2 mb-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">VA %</label>
                    {canEditRate ? (
                      <input type="number" onFocus={e => e.target.select()} value={wastagePercent} onChange={e => setWastagePercent(e.target.value)}
                        className="w-full border border-amber-400 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="0" />
                    ) : (
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {wastagePercent || <span className="text-slate-300">—</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">VA Amt</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {wastageAmount ? `₹${parseFloat(wastageAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Making ₹/g</label>
                    {canEditRate ? (
                      <input type="number" onFocus={e => e.target.select()} value={makingCharge} onChange={e => setMakingCharge(e.target.value)}
                        className="w-full border border-amber-400 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="0" />
                    ) : (
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {makingCharge || <span className="text-slate-300">—</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Making Amt</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {makingAmount ? `₹${parseFloat(makingAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                </div>
                {/* Total Gold */}
                <div className="flex items-center justify-between rounded-lg px-4 py-2 bg-slate-50 border border-slate-200">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Total Gold</span>
                  <span className="text-sm font-bold text-slate-800">
                    {totalGoldAmount ? `₹${parseFloat(totalGoldAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Stones */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-600 rounded-t-xl">
                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                </svg>
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Stones</span>
              </div>
              <StoneSection
                onStoneTotalChange={handleStoneTotalChange}
                initialStones={fetchedStones}
              />
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-600 rounded-t-xl">
                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Notes</span>
              </div>
              <div className="px-4 py-3">
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows="2"
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white resize-none"
                  placeholder="Special notes or requirements…" />
              </div>
            </div>

          </div>

          {/* ══ RIGHT COLUMN — sticky Price Breakdown + Item Image ══ */}
          <div className="xl:col-span-1 flex flex-col gap-3 sticky top-[60px] self-start">
            <USDEstimateTotalSection
              key={formResetKey}
              totalGoldAmount={totalGoldAmount}
              goldValue={goldValue}
              wastageAmount={wastageAmount}
              makingAmount={makingAmount}
              stoneTotal={stoneCost}
              stonePurchaseCost={stonePurchaseCost}
              dollarRate={dollarRate}
              diamondCarats={diamondCarats}
              certificationRequired={certificationRequired}
              categoryInfo={categoryInfo}
              setParentGrandTotalUSD={setGrandTotalUSD}
              onTotalsChange={setPdfTotals}
              netWeight={costPanelNetWeight}
              goldRate={goldRate}
              itemCode={itemCode}
              customerResetSeq={customerResetSeq}
              finePercent={finePercent}
              kundanWeight={kundanWeight}
              kundanPercent={kundanPercent}
              purity={purity}
              entryRates={entryRates}
              todaysGoldRate={todaysGoldRate}
              todaysDollarRate={todaysDollarRate}
              billDate={date}
              initialDiscount={loadedDiscount}
              initialCustomsPct={loadedCustomsPct}
            />

            {/* On-screen only: cumulative price reduction vs. standard/catalog price.
                Never sent to the PDF/email/WhatsApp payloads — screen-only context for staff. */}
            {catalogPriceINR != null && (() => {
              const currentINR = Math.ceil(parseFloat(pdfTotals?.grandTotalINR) || 0);
              const reduced = catalogPriceINR - currentINR;
              if (currentINR <= 0 || reduced <= 0) return null;
              return (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-xs">
                  <div className="flex justify-between text-slate-500">
                    <span>Standard Price</span>
                    <span>₹{catalogPriceINR.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-emerald-700 font-semibold mt-1">
                    <span>You've Reduced</span>
                    <span>₹{reduced.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              );
            })()}

            {/* Item Image */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-600 rounded-t-xl">
                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-slate-200 font-semibold text-xs tracking-widest uppercase">Item Image</span>
              </div>
              {jewelryImage ? (
                <img src={jewelryImage} alt={jewelryName || 'Jewelry'} className="w-full object-contain" />
              ) : (
                <div className="mx-4 my-3 rounded-lg bg-slate-50 border border-slate-100 aspect-square flex flex-col items-center justify-center gap-2">
                  <svg className="w-8 h-8 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-slate-300 text-xs">Enter item code to load image</span>
                </div>
              )}
            </div>
          </div>

        </div>

      </div>

      {/* Detail Modal */}
      <EstimateDetailModal
        isOpen={showDetailModal}
        estimate={{
          estimate_number: 'DRAFT', customer_name: customerName || 'Customer',
          customer_phone: customerPhone, customer_email: customerEmail,
          customer_city: customerCity, customer_state: customerState,
          date, valid_until: validUntil, item_code: itemCode,
          jewelry_name: jewelryName, gold_purity: purity,
          gross_weight: grossWeight, net_weight: netWeight, fine_weight: fineWeight,
          gold_rate: goldRate, gold_price: goldPrice, gold_value: goldValue,
          wastage_percent: wastagePercent, wastage_amount: wastageAmount,
          making_charge_per_gram: makingCharge, making_amount: makingAmount,
          total_gold_amount: totalGoldAmount, stone_total: stoneTotal,
          diamond_carats: diamondCarats, certification_required: certificationRequired,
          currency: 'USD', usd_to_inr_rate: dollarRate,
          grand_total_usd: grandTotalUSD, image_url: jewelryImage, notes
        }}
        onClose={() => setShowDetailModal(false)}
      />
    </div>
  );
}

// Stone Section Component (same as billing)
function StoneSection({ onStoneTotalChange, initialStones = [] }) {
  const { hasPermission } = usePermissions();
  const canEditRate = hasPermission('usd-estimate', 'edit');
  const [stones, setStones] = useState([]);

  useEffect(() => {
    if (Array.isArray(initialStones) && initialStones.length > 0) {
      setStones(initialStones.map((stone, idx) => {
        const weight = parseFloat(stone.weight) || 0;
        const unit = stone.unit || 'CARAT';
        return {
          id: stone.id || idx,
          code: stone.stone_code || '',
          name: stone.stone_name || '',
          weight,
          unit,
          purchaseRate: parseFloat(stone.cost_price) || 0,
          rate: parseFloat(stone.sale_price) || 0,
          cost: weight * (parseFloat(stone.sale_price) || 0),
          purchaseCost: weight * (parseFloat(stone.cost_price) || 0),
        };
      }));
    } else {
      setStones([]);
    }
  }, [initialStones]);

  useEffect(() => {
    const totalWeight = stones.reduce((sum, stone) => sum + stoneWeightToGrams(stone.weight, stone.unit), 0);
    const totalCost = stones.reduce((sum, stone) => sum + parseFloat(stone.cost || 0), 0);
    const purchaseTotal = stones.reduce((sum, stone) => sum + parseFloat(stone.purchaseCost || 0), 0);
    const categoryTotals = buildStoneCategoryTotals(stones);
    onStoneTotalChange({ weight: totalWeight, cost: totalCost, purchaseTotal, categoryTotals, stones });
  }, [stones, onStoneTotalChange]);

  const updateStone = (id, field, value) => {
    setStones(stones.map(stone => {
      if (stone.id === id) {
        const updatedStone = { ...stone, [field]: parseFloat(value) || 0 };
        if (field === 'rate') {
          updatedStone.cost = updatedStone.weight * updatedStone.rate;
        }
        return updatedStone;
      }
      return stone;
    }));
  };

  const unitLabel = (unit) => {
    const u = (unit || '').toUpperCase();
    if (u === 'CARAT' || u === 'CARATS' || u === 'CT') return 'Ct';
    if (u === 'GRAM' || u === 'GRAMS' || u === 'G') return 'g';
    if (u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER') return 'pcs';
    return unit || 'Ct';
  };

  return (
    <div>
      {stones.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-slate-700 text-white">
                <th className="px-3 py-2 text-left font-semibold tracking-wide">Code</th>
                <th className="px-3 py-2 text-left font-semibold tracking-wide">Name</th>
                <th className="px-3 py-2 text-center font-semibold tracking-wide">Weight</th>
                <th className="px-3 py-2 text-center font-semibold tracking-wide">Unit</th>
                <th className="px-3 py-2 text-right font-semibold tracking-wide">Rate (₹)</th>
                <th className="px-3 py-2 text-right font-semibold tracking-wide">Amount (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...stones].sort((a, b) => {
                const unitOrder = u => { const x = (u || '').toUpperCase(); return (x === 'CARAT' || x === 'CT') ? 0 : (x === 'GRAM' || x === 'G') ? 1 : 2; };
                const uDiff = unitOrder(a.unit) - unitOrder(b.unit);
                if (uDiff !== 0) return uDiff;
                return (a.code || '').localeCompare(b.code || '');
              }).map((stone, idx) => (
                <tr key={stone.id} className={idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100/60'}>
                  <td className="px-3 py-1.5 font-mono font-semibold text-slate-700">{stone.code}</td>
                  <td className="px-3 py-1.5 text-slate-600">{stone.name}</td>
                  <td className="px-3 py-1.5 text-center text-slate-700">
                    {unitLabel(stone.unit) === 'pcs' ? stone.weight : parseFloat(stone.weight).toFixed(3)}
                  </td>
                  <td className="px-3 py-1.5 text-center text-slate-500">{unitLabel(stone.unit)}</td>
                  <td className="px-2 py-1 text-right">
                    {canEditRate ? (
                      <input
                        type="number" onFocus={e => e.target.select()}
                        value={stone.rate}
                        onChange={e => updateStone(stone.id, 'rate', e.target.value)}
                        className="w-24 text-right border border-slate-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                        min="0"
                        step="1"
                      />
                    ) : (
                      <span className="text-slate-700 font-medium">
                        ₹{parseFloat(stone.rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold text-slate-800">
                    ₹{parseFloat(stone.cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-300">
                <td colSpan={5} className="px-3 py-2 text-xs font-bold text-slate-600 uppercase tracking-wide text-right">Total Stone Amount</td>
                <td className="px-3 py-2 text-right font-bold text-slate-800">
                  ₹{stones.reduce((sum, s) => sum + (s.cost || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-5 py-4 text-slate-400">
          <svg className="w-5 h-5 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3l14 9-14 9V3z" />
          </svg>
          <p className="text-xs text-slate-400">No stones — enter item code to load</p>
        </div>
      )}
    </div>
  );
}

// Total Section Component for USD Estimates (similar to USBilling)
function USDEstimateTotalSection({ totalGoldAmount, goldValue, wastageAmount, makingAmount, stoneTotal, stonePurchaseCost, dollarRate, diamondCarats, certificationRequired, categoryInfo, setParentGrandTotalUSD, onTotalsChange, netWeight, goldRate, itemCode, customerResetSeq, finePercent, kundanWeight, kundanPercent, purity, entryRates = { goldRate: null, dollarRate: null, shipmentCostUSD: 0, laborCharges: 0, otherCosts: 0, inventoryNetWeight: '' }, todaysGoldRate = null, todaysDollarRate = null, billDate = '', initialDiscount = 0, initialCustomsPct = null }) {
  const { hasPermission } = usePermissions();
  const isUser = !hasPermission('usd-estimate', 'edit');
  const [certChargePerCarat, setCertChargePerCarat] = useState(0);
  const [totalTaxPercent, setTotalTaxPercent] = useState(13.75);
  const [origTaxPercent, setOrigTaxPercent] = useState(13.75);

  // Starting a new customer (Clear) discards any manual Customs % edit and
  // resets to the fetched default rate. Adding another item for the SAME
  // customer must NOT reset it — the edited rate carries across their items.
  useEffect(() => {
    setTotalTaxPercent(origTaxPercent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerResetSeq]);

  // Restore a loaded estimate's Customs % — initialCustomsPct arrives after
  // mount (handleLoadEstimate fires async), so re-sync whenever it changes.
  useEffect(() => {
    if (initialCustomsPct != null && parseFloat(initialCustomsPct) > 0) {
      setTotalTaxPercent(parseFloat(initialCustomsPct));
    }
  }, [initialCustomsPct]);
  const [taxTotal, setTaxTotal] = useState('0.00');
  const [grandTotal, setGrandTotal] = useState('0.00');
  const [grandTotalUSD, setGrandTotalUSD] = useState('0.00');
  const [calculatedCertCharges, setCalculatedCertCharges] = useState(0);
  const [discount, setDiscount] = useState(initialDiscount || 0);

  // Restore a loaded estimate's discount — initialDiscount arrives after
  // mount (handleLoadEstimate fires async), so seed via useState above isn't
  // enough; re-sync whenever it changes.
  useEffect(() => {
    if (initialDiscount > 0) setDiscount(initialDiscount);
  }, [initialDiscount]);

  // Hold-to-peek cost panel (hold backtick ` key) — admin/manager only
  const [showCostPanel, setShowCostPanel] = useState(false);
  useEffect(() => {
    if (isUser) return; // disabled for role=user
    const onDown = (e) => { if (e.key === '`') { e.preventDefault(); if (!e.repeat) setShowCostPanel(true); } };
    const onUp = (e) => { if (e.key === '`') setShowCostPanel(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, [isUser]);

  // Session-based authentication

  // Update certification charge per carat when category changes
  useEffect(() => {
    if (categoryInfo && categoryInfo.certification_charges) {
      setCertChargePerCarat(categoryInfo.certification_charges);
    } else {
      setCertChargePerCarat(0);
    }
  }, [categoryInfo]);

  // Fetch tax rates on mount
  useEffect(() => {
    fetch('/api/rates/tax/latest', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate) {
          const customsDuty = parseFloat(data.rate.customs_duty) || 0;
          const stateTax = parseFloat(data.rate.state_tax) || 0;
          const combined = customsDuty + stateTax;
          setTotalTaxPercent(combined);
          setOrigTaxPercent(combined);
        }
      })
      .catch(() => {
        // Keep default value on error
      });
  }, []);

  // Small items ("V" category) are priced as cost + 30%, rounded up to $100 —
  // NOT by the normal breakdown. `smallItem` gates that path below.
  const smallItem = isSmallItemCode(itemCode);

  // Canonical ACTUAL COST in ₹ (gold + stones + labor + cert + ~18% customs),
  // identical to the hidden Cost Panel. Computed every render so it can drive
  // the small-item price. Cost logic itself is unchanged.
  const actualCostINR = useMemo(() => {
    const isKundan = isKundanItemCode(itemCode);
    const nw = parseFloat(entryRates.inventoryNetWeight) || parseFloat(netWeight) || 0; // Cost Panel: physical inventory Net Wt
    const rate24k = parseFloat(goldRate) || 0;
    const currentGoldPrice = calculatePurityGoldRate(rate24k, purity);
    const currentGoldValue = calculateGoldValue(currentGoldPrice, nw);
    const kw = parseFloat(kundanWeight) || 0;
    const goldNetWt = parseFloat(entryRates.inventoryNetWeight) > 0 ? nw : Math.max(0, nw - kw);
    const kundanFineWt = isKundan && finePercent > 0
      ? calculateKundanFineWeight(goldNetWt, finePercent, kw, kundanPercent)
      : 0;
    const pureGoldAmtRaw = isKundan && kundanFineWt > 0
      ? Math.ceil(kundanFineWt * rate24k)
      : Math.ceil(currentGoldValue);
    const pureGoldAmt = applyKundanGoldCostDeduction(pureGoldAmtRaw, itemCode); // Kundan: −6% (US only)
    const stoneCostAmt = Math.ceil(parseFloat(stonePurchaseCost || 0));
    const certAmt = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificationRequired);
    const laborCost = Math.ceil(nw * (entryRates.laborCharges || 0));
    const { totalCost } = calculateAcquisitionCostWithTax({ goldCost: pureGoldAmt, stoneCost: stoneCostAmt, laborCost, certCost: certAmt, otherCost: entryRates.otherCosts, taxPercent: 18 });
    return totalCost;
  }, [itemCode, netWeight, goldRate, purity, kundanWeight, kundanPercent, finePercent, stonePurchaseCost, diamondCarats, certChargePerCarat, certificationRequired, entryRates]);

  // The auto small-item price (USD). 0 until cost + a live dollar rate exist.
  const smallItemPriceUSD = useMemo(() => {
    if (!smallItem) return 0;
    const dr = parseFloat(dollarRate) || 0;
    if (dr <= 0 || actualCostINR <= 0) return 0;
    return calculateSmallItemPrice(actualCostINR / dr, 100);
  }, [smallItem, dollarRate, actualCostINR]);

  // Calculate certification charges and totals
  useEffect(() => {
    const certAmt = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificationRequired);
    setCalculatedCertCharges(certAmt);

    const jewelValue = calculateJewelValue(totalGoldAmount, stoneTotal, certAmt);
    const normal = calculateUSDGrandTotal(jewelValue, totalTaxPercent, dollarRate);

    // For V items the customer price is the fixed cost+30% figure; tax is already
    // inside actualCost, so we don't add it again on top.
    const dr = parseFloat(dollarRate) || 1;
    // V items: cost + 30%, or the normal price when staff-raised making / VA /
    // stone rates push it higher (leaves room to negotiate down).
    const vPriceUSD = Math.max(smallItemPriceUSD, normal.grandTotalUSD);
    const gtUSD = smallItem && smallItemPriceUSD > 0 ? vPriceUSD : normal.grandTotalUSD;
    const grandTotalINR = smallItem && smallItemPriceUSD > 0 ? Math.ceil(vPriceUSD * dr) : normal.grandTotalINR;
    const taxAmountINR = smallItem && smallItemPriceUSD > 0 ? 0 : normal.taxAmountINR;

    setTaxTotal(taxAmountINR.toFixed(2));
    setGrandTotal(grandTotalINR.toFixed(2));
    setGrandTotalUSD(gtUSD.toFixed(2));

    const discountUSD = Math.min(parseFloat(discount) || 0, gtUSD);
    const finalUSD = Math.max(0, gtUSD - discountUSD);

    if (setParentGrandTotalUSD) {
      setParentGrandTotalUSD(finalUSD.toFixed(2));
    }
    if (onTotalsChange) {
      onTotalsChange({
        totalTaxPercent: parseFloat(totalTaxPercent) || 0,
        origTaxPercent: parseFloat(origTaxPercent) || 13.75,
        taxTotalINR: taxAmountINR.toFixed(2),
        grandTotalINR: grandTotalINR.toFixed(2),
        certCharges: certAmt,
        discountAmount: discountUSD,
        smallItem: smallItem && smallItemPriceUSD > 0,
      });
    }
  }, [totalGoldAmount, stoneTotal, certChargePerCarat, totalTaxPercent, origTaxPercent, dollarRate, certificationRequired, diamondCarats, discount, setParentGrandTotalUSD, onTotalsChange, smallItem, smallItemPriceUSD]);

  return (
    <div className="space-y-3">

      {/* ── Price Breakdown Card ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
          <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
          </svg>
          <span className="text-white font-semibold text-xs tracking-widest uppercase">Price Breakdown</span>
        </div>

        {/* Line items — hidden for small ("V") items: only weights + final price shown */}
        {!smallItem && (
        <div className="divide-y divide-slate-100">
          {/* Gold Amount */}
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
              <span className="text-sm text-slate-600">Gold Amount</span>
            </div>
            <span className="text-sm font-semibold text-slate-800">
              ₹{Math.ceil(parseFloat(totalGoldAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Stone Cost — only if non-zero */}
          {parseFloat(stoneTotal || 0) > 0 && (
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                <span className="text-sm text-slate-600">Stone Cost</span>
              </div>
              <span className="text-sm font-semibold text-slate-800">
                ₹{Math.ceil(parseFloat(stoneTotal || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Certification — only if non-zero */}
          {calculatedCertCharges > 0 && (
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                <span className="text-sm text-slate-600">Certification</span>
                {certChargePerCarat > 0 && (
                  <span className="text-xs text-slate-400">(₹{parseFloat(certChargePerCarat).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/ct)</span>
                )}
              </div>
              <span className="text-sm font-semibold text-slate-800">
                ₹{calculatedCertCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Subtotal */}
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
            <span className="text-sm font-medium text-slate-700">Subtotal</span>
            <span className="text-sm font-bold text-slate-800">
              ₹{(Math.ceil(parseFloat(totalGoldAmount || 0)) + Math.ceil(parseFloat(stoneTotal || 0)) + calculatedCertCharges).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Tax Row — editable % inline (manager/admin/super_admin only) */}
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
              <span className="text-sm text-slate-600">Customs</span>
              {!isUser ? (
                <div className="flex items-center border border-amber-400 rounded-md overflow-hidden">
                  <input
                    type="number" onFocus={e => e.target.select()}
                    value={totalTaxPercent || ''}
                    onChange={e => setTotalTaxPercent(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-14 text-center text-xs py-0.5 px-1 focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                  />
                  <span className="bg-amber-50 px-1.5 py-0.5 text-xs text-amber-600 border-l border-amber-400">%</span>
                </div>
              ) : (
                <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded px-2 py-0.5">{totalTaxPercent || 0}%</span>
              )}
            </div>
            <span className="text-sm font-semibold text-slate-700">
              ₹{parseInt(taxTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
        )}

        {/* Grand Total INR footer — hidden for small items (USD price only) */}
        {!smallItem && (
        <div className="bg-slate-600 px-4 py-2 flex items-center justify-between">
          <p className="text-slate-200 text-xs uppercase tracking-widest font-medium">Grand Total (INR)</p>
          <span className="text-white text-base font-bold tracking-tight">
            ₹{parseInt(grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        )}

        {/* Grand Total USD footer */}
        <div className="bg-slate-800 px-4 py-2.5 flex items-center justify-between">
          <p className="text-slate-300 text-xs uppercase tracking-widest font-medium">Grand Total (USD)</p>
          <span className="text-white text-xl font-bold tracking-tight">
            ${parseFloat(grandTotalUSD || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* Discount row */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-300">Round Off</span>
            <div className="flex items-center border border-green-500 rounded overflow-hidden">
              <span className="bg-slate-600 px-1.5 py-0.5 text-xs text-green-400 border-r border-green-500">$</span>
              <input
                type="number" onFocus={e => e.target.select()}
                value={discount || ''}
                onChange={e => setDiscount(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                placeholder="0"
                min="0"
                step="0.01"
                className="w-20 text-center text-xs py-0.5 px-1 focus:outline-none bg-slate-700 text-white placeholder-slate-400"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const cur = parseFloat(grandTotalUSD) || 0;
                const disc = Math.round((cur % 1) * 100) / 100;
                setDiscount(disc);
              }}
              className="text-xs px-2 py-0.5 rounded border border-green-500 bg-slate-600 text-green-400 hover:bg-slate-500 transition whitespace-nowrap"
            >
              Round ↓$1
            </button>
          </div>
          {parseFloat(discount) > 0 && (
            <span className="text-sm font-semibold text-green-400">
              −${parseFloat(discount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </div>

        {/* Final Amount */}
        {parseFloat(discount) > 0 && (() => {
          const finalUSD = Math.max(0, (parseFloat(grandTotalUSD) || 0) - (parseFloat(discount) || 0));
          const finalINR = Math.round(finalUSD * (parseFloat(dollarRate) || 0));
          return (
            <div className="bg-emerald-700 px-4 py-2.5 flex items-center justify-between">
              <p className="text-emerald-100 text-xs uppercase tracking-widest font-medium">Final Amount</p>
              <div className="text-right">
                <p className="text-white text-xl font-bold tracking-tight">${finalUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-emerald-200 text-xs mt-0.5">≈ ₹{finalINR.toLocaleString('en-IN')}</p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── USD / INR conversion pill ── */}
      <div className="flex gap-2">
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-center">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Rate Used</p>
          <p className="text-sm font-bold text-slate-700 mt-0.5">₹{parseFloat(dollarRate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / $1</p>
        </div>
        <div className="flex-1 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-center">
          <p className="text-xs text-green-600 font-medium uppercase tracking-wide">USD Total</p>
          <p className="text-sm font-bold text-green-700 mt-0.5">${parseFloat(grandTotalUSD || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      {/* ── HOLD-TO-PEEK COST PANEL (print:hidden, owner only) ── */}
      {showCostPanel && (() => {
        const isKundan = isKundanItemCode(itemCode);
        const nw = parseFloat(entryRates.inventoryNetWeight) || parseFloat(netWeight) || 0; // Cost Panel: physical inventory Net Wt
        // Always use current live rates for gold cost and dollar conversion
        const rate24k = parseFloat(goldRate) || 0;
        const entryDR = parseFloat(dollarRate) || 1;
        const currentGoldPrice = calculatePurityGoldRate(rate24k, purity);
        const currentGoldValue = calculateGoldValue(currentGoldPrice, nw);
        const kw = parseFloat(kundanWeight) || 0;
        const goldNetWt = parseFloat(entryRates.inventoryNetWeight) > 0 ? nw : Math.max(0, nw - kw);
        const kundanFineWt = isKundan && finePercent > 0
          ? calculateKundanFineWeight(goldNetWt, finePercent, kw, kundanPercent)
          : 0;
        const pureGoldAmtRaw = isKundan && kundanFineWt > 0
          ? Math.ceil(kundanFineWt * rate24k)
          : Math.ceil(currentGoldValue);
        const pureGoldAmt = applyKundanGoldCostDeduction(pureGoldAmtRaw, itemCode); // Kundan: −6% (US only)
        const stoneCostAmt = Math.ceil(parseFloat(stonePurchaseCost || 0));
        const certAmt = calculatedCertCharges;
        const laborCost = Math.ceil(nw * (entryRates.laborCharges || 0));
        const { preTaxCost: preTaxCostINR, taxAmount: taxAmtINR } = calculateAcquisitionCostWithTax({ goldCost: pureGoldAmt, stoneCost: stoneCostAmt, laborCost, certCost: certAmt, otherCost: entryRates.otherCosts, taxPercent: 18 });
        const taxLabel = 'Tax (Customs ~18%)';
        const quotedCostINR = parseFloat(grandTotal || 0);
        const {
          actualCost: actualCostINR, profit, profitPct,
          actualCostUSD = 0, quotedCostUSD = 0, profitUSD = 0,
        } = calculateCostPanelResult({ preTaxCost: preTaxCostINR, taxAmount: taxAmtINR, quotedCost: quotedCostINR, dollarRate });
        return (
          <CostPanel
            usePortal
            positionClassName="top-[72px] right-4"
            ratePill={
              <RatePill
                todayGoldRate={todaysGoldRate}
                usedGoldRate={rate24k}
                todayDollarRate={todaysDollarRate}
                usedDollarRate={entryDR}
                usedDate={billDate}
              />
            }
            rows={[
              ...(isKundan && kundanFineWt > 0 ? [{
                label: 'Fine Wt',
                raw: `${kundanFineWt.toFixed(3)} g × ₹${rate24k.toLocaleString('en-IN')}/g`,
                muted: true,
              }] : []),
              { label: isKundan ? 'Gold Cost (Kundan −6%)' : 'Gold Cost', value: pureGoldAmt },
              { label: 'Stones Cost', value: stoneCostAmt },
              { label: `Labor${entryRates.laborCharges ? ` (₹${entryRates.laborCharges}/g)` : ''}`, value: laborCost },
              { label: 'Certificate', value: certAmt },
              { label: 'Other Costs', value: entryRates.otherCosts || 0 },
              { label: taxLabel, value: taxAmtINR },
            ]}
            quotedCost={quotedCostINR}
            actualCost={actualCostINR}
            quotedCostUSD={quotedCostUSD}
            actualCostUSD={actualCostUSD}
            profit={profit}
            profitPct={profitPct}
            profitUSD={profitUSD}
          />
        );
      })()}
    </div>
  );
}

export default USDEstimate;
